import dayjs from 'dayjs';

import {
  DISCLOSURE_AUTH_CONSENT,
  TERRA_CONSENT,
  VALIDIC_CONSENT,
  VITALL_CONSENT,
} from '@constants/consents';

import { ConsentRecord, ConsentScopeCode } from '@models/consent';
import type { CategoryType } from '@models/legal-directive';

import { formatPatientId } from '@utils/format';

import type { ConsentModelInput, Extension } from 'generated/types';
import { ConsentStatus } from 'generated/types';

export function getVitallConsentTemplate(
  patient_id: string,
  enabled: boolean
): ConsentModelInput {
  return {
    resourceType: 'Consent',
    patient: {
      reference: formatPatientId(patient_id),
    },
    dateTime: dayjs().format('YYYY-MM-DD'),
    scope: {
      coding: [
        {
          code: ConsentScopeCode.PatientPrivacy,
          display: 'Privacy Consent',
          system: 'http://terminology.hl7.org/CodeSystem/consentscope',
        },
      ],
      text: 'Privacy Consent',
    },
    category: [
      {
        coding: [
          {
            code: VITALL_CONSENT,
            display: 'VITALL Privacy Agreement',
            system: ConsentRecord.VITALL_SYSTEM,
          },
        ],
        text: 'VITALL Privacy Agreement',
      },
    ],
    status: enabled ? ConsentStatus.active : ConsentStatus.inactive,
    policyRule: {
      coding: [
        {
          system:
            'https://terminology.hl7.org/5.0.0/ValueSet-v3-ActConsentDirective.html',
          code: '_ActConsentDirective',
          display: 'ActConsentDirective',
        },
      ],
    },
  };
}

export function getValidicConsentTemplate(
  patient_id: string,
  enabled: boolean
): ConsentModelInput {
  return {
    resourceType: 'Consent',
    patient: {
      reference: formatPatientId(patient_id),
    },
    dateTime: dayjs().format('YYYY-MM-DD'),
    scope: {
      coding: [
        {
          code: ConsentScopeCode.PatientPrivacy,
          display: 'Privacy Consent',
          system: 'http://terminology.hl7.org/CodeSystem/consentscope',
        },
      ],
      text: 'Privacy Consent',
    },
    category: [
      {
        coding: [
          {
            code: VALIDIC_CONSENT,
            display: 'Validic Device Data Collection',
            system: ConsentRecord.VITALL_SYSTEM,
          },
        ],
        text: 'VITALL Privacy Agreement',
      },
    ],
    status: enabled ? ConsentStatus.active : ConsentStatus.inactive,
    policyRule: {
      coding: [
        {
          system:
            'https://terminology.hl7.org/5.0.0/ValueSet-v3-ActConsentDirective.html',
          code: '_ActConsentDirective',
          display: 'ActConsentDirective',
        },
      ],
    },
  };
}

export function getTerraConsentTemplate(
  patient_id: string,
  enabled: boolean
): ConsentModelInput {
  return {
    resourceType: 'Consent',
    patient: {
      reference: formatPatientId(patient_id),
    },
    dateTime: dayjs().format('YYYY-MM-DD'),
    scope: {
      coding: [
        {
          code: ConsentScopeCode.PatientPrivacy,
          display: 'Privacy Consent',
          system: 'http://terminology.hl7.org/CodeSystem/consentscope',
        },
      ],
      text: 'Privacy Consent',
    },
    category: [
      {
        coding: [
          {
            code: TERRA_CONSENT,
            display: 'Terra Device Data Collection',
            system: ConsentRecord.VITALL_SYSTEM,
          },
        ],
        text: 'VITALL Privacy Agreement',
      },
    ],
    status: enabled ? ConsentStatus.active : ConsentStatus.inactive,
    policyRule: {
      coding: [
        {
          system:
            'https://terminology.hl7.org/5.0.0/ValueSet-v3-ActConsentDirective.html',
          code: '_ActConsentDirective',
          display: 'ActConsentDirective',
        },
      ],
    },
  };
}

export function getLegalDirectiveTemplate(
  patient_id: string,
  type: CategoryType,
  selections: Extension[]
): ConsentModelInput {
  const legalDirFriendlyNameMap: Record<CategoryType, string> = {
    'blood-products': 'Blood Products',
    'heroic-measures': 'Heroic Measures',
    'specific-organs': 'Specific Organs',
  };
  return {
    resourceType: 'Consent',
    patient: {
      reference: formatPatientId(patient_id),
    },
    dateTime: dayjs().format('YYYY-MM-DD'),
    scope: {
      coding: [
        {
          code: ConsentScopeCode.Adr,
          display: 'Advanced Care Directive',
          system: 'http://terminology.hl7.org/CodeSystem/consentscope',
        },
      ],
      text: 'Advanced Care Directive',
    },
    category: [
      {
        coding: [
          {
            code: type,
            display: legalDirFriendlyNameMap[type],
            system: ConsentRecord.VITALL_SYSTEM,
          },
        ],
        text: legalDirFriendlyNameMap[type],
      },
    ],
    status: ConsentStatus.active,
    extension: selections,
    policyRule: {
      coding: [
        {
          system:
            'https://terminology.hl7.org/5.0.0/ValueSet-v3-ActConsentDirective.html',
          code: '_ActConsentDirective',
          display: 'ActConsentDirective',
        },
      ],
    },
  };
}

export function getDisclosureAuthConsentTemplate(
  patient_id: string
): ConsentModelInput {
  return {
    resourceType: 'Consent',
    patient: {
      reference: formatPatientId(patient_id),
    },
    dateTime: dayjs().format('YYYY-MM-DD'),
    scope: {
      coding: [
        {
          code: ConsentScopeCode.PatientPrivacy,
          display: 'Privacy Consent',
          system: 'http://terminology.hl7.org/CodeSystem/consentscope',
        },
      ],
      text: 'Privacy Consent',
    },
    category: [
      {
        coding: [
          {
            code: DISCLOSURE_AUTH_CONSENT,
            display: 'Disclosure Authorization Document',
            system: ConsentRecord.VITALL_SYSTEM,
          },
        ],
        text: 'Disclosure Authorization Document',
      },
    ],
    status: ConsentStatus.active,
    policyRule: {
      coding: [
        {
          system:
            'https://terminology.hl7.org/5.0.0/ValueSet-v3-ActConsentDirective.html',
          code: '_ActConsentDirective',
          display: 'ActConsentDirective',
        },
      ],
    },
  };
}
